import { Alert, Button, Card, Field, Input, Logo } from '@platform/ui';
import { useState, type FormEvent, type ReactNode } from 'react';

import { PlatformTag } from '../components/PlatformTag';
import { useMessages } from '../i18n';
import { ApiError } from '../lib/api';
import { usePageTitle } from '../lib/page-title';
import { problemCode, problemText } from '../lib/problems';
import { useCompleteSecondFactor, useLogin, useSession } from '../lib/session';

/**
 * Staff sign-in.
 *
 * The same credentials as the seller panel, because a person has one account.
 * What differs is what happens next: a correct password that belongs to
 * somebody who is not platform staff is told exactly that, rather than being
 * shown an empty platform or a misleading "wrong password".
 */
export function SignInPage() {
    const t = useMessages();
    usePageTitle(t.auth.title);
    const login = useLogin();
    const { me, isStaff } = useSession();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    function onSubmit(event: FormEvent) {
        event.preventDefault();
        login.mutate({ email, password });
    }

    const message =
        login.error instanceof ApiError
            ? login.error.isUnauthenticated
                ? t.auth.invalid
                : problemText(t, login.error)
            : login.error
              ? t.genericError
              : null;

    // Someone who turned on two-step sign-in finishes it with a code
    // (docs/07 §4). It is optional for staff as for everyone: enrolling is
    // offered on Hesabım, never required before the panel opens.
    const secondFactor = login.data?.secondFactor;
    if (secondFactor) {
        return (
            <SignInFrame>
                <SecondFactorStep
                    token={secondFactor.token}
                    onStartOver={() => {
                        login.reset();
                        setPassword('');
                    }}
                />
            </SignInFrame>
        );
    }

    return (
        <SignInFrame>
            <Card title={t.auth.title} description={t.auth.subtitle}>
                <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
                    {message && <Alert tone="danger">{message}</Alert>}

                    {/* Signed in, but not staff. Said plainly: the credentials were
                            right, and pretending otherwise would send someone to reset a
                            password that works. */}
                    {me && !isStaff && <Alert tone="warning">{t.auth.notStaff}</Alert>}

                    <Field label={t.auth.email} required>
                        {({ id, describedBy }) => (
                            <Input
                                id={id}
                                aria-describedby={describedBy}
                                type="email"
                                autoComplete="username"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                            />
                        )}
                    </Field>

                    <Field label={t.auth.password} required>
                        {({ id, describedBy }) => (
                            <Input
                                id={id}
                                aria-describedby={describedBy}
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                            />
                        )}
                    </Field>

                    <Button type="submit" block loading={login.isPending}>
                        {t.auth.submit}
                    </Button>
                </form>
            </Card>
        </SignInFrame>
    );
}

function SignInFrame({ children }: { children: ReactNode }) {
    return (
        <div className="grid min-h-dvh place-items-center bg-muted p-6">
            <div className="grid w-full max-w-104 gap-5">
                <div className="flex items-center gap-2">
                    <Logo />
                    <PlatformTag />
                </div>
                {children}
            </div>
        </div>
    );
}

/** The code that finishes a staff sign-in; a spent sign-in starts again. */
function SecondFactorStep({ token, onStartOver }: { token: string; onStartOver: () => void }) {
    const t = useMessages();
    const s = t.auth.secondFactor;
    const complete = useCompleteSecondFactor();
    const [code, setCode] = useState('');

    async function onSubmit(event: FormEvent) {
        event.preventDefault();
        try {
            await complete.mutateAsync({ token, code: code.trim() });
        } catch {
            // Said above the field, from the mutation's own error state.
        }
    }

    const expired = problemCode(complete.error) === 'auth.second_factor_expired';
    return (
        <Card title={s.title} description={s.hint}>
            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
                {complete.isError && <Alert tone="danger">{problemText(t, complete.error)}</Alert>}
                {expired ? (
                    <Button type="button" block onClick={onStartOver}>
                        {s.startOver}
                    </Button>
                ) : (
                    <>
                        <Field label={s.code} required>
                            {({ id }) => (
                                <Input
                                    id={id}
                                    autoComplete="one-time-code"
                                    autoFocus
                                    value={code}
                                    onChange={(event) => setCode(event.target.value)}
                                />
                            )}
                        </Field>
                        <Button type="submit" block loading={complete.isPending || complete.isSuccess} disabled={code.trim().length < 6}>
                            {s.submit}
                        </Button>
                    </>
                )}
            </form>
        </Card>
    );
}
